import React, { useState } from 'react';
import { Head, useForm, router } from '@inertiajs/react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Key, AlertCircle, Eye, EyeOff, Trash2 } from 'lucide-react';

// Simple time ago formatter
function formatTimeAgo(dateString) {
  if (!dateString) return 'Never used';
  
  const date = new Date(dateString);
  const now = new Date();
  const seconds = Math.floor((now - date) / 1000);
  
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} minute${minutes !== 1 ? 's' : ''} ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours !== 1 ? 's' : ''} ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days} day${days !== 1 ? 's' : ''} ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months} month${months !== 1 ? 's' : ''} ago`;
  const years = Math.floor(months / 12);
  return `${years} year${years !== 1 ? 's' : ''} ago`;
}

export default function ApiKeysIndex({ api_keys, can_create, can_destroy, flash = {}, auth }) {
  const [revealedSecrets, setRevealedSecrets] = useState({});
  const { post, delete: destroy } = useForm();

  const toggleSecretVisibility = (apiKeyId) => {
    setRevealedSecrets(prev => ({
      ...prev,
      [apiKeyId]: !prev[apiKeyId]
    }));
  };

  const handleCreateApiKey = () => {
    if (!auth.user) {
      console.error('User not authenticated');
      return;
    }
    
    post(`/account/users/${auth.user.id}/api_keys`, {
      preserveScroll: true,
    });
  };

  const handleDeleteApiKey = (apiKeyId) => {
    if (confirm('Are you sure you want to revoke this API key? This action cannot be undone.')) {
      destroy(`/account/api_keys/${apiKeyId}`, {
        preserveScroll: true,
      });
    }
  };

  return (
    <>
      <Head title="Manage API Keys" />
      
      <div className="container mx-auto py-6 px-4 max-w-6xl">
        <div className="mb-8">
          <h1 className="text-4xl font-bold mb-2">API Keys</h1>
          <p className="text-lg text-gray-600">
            Manage your private API keys to upload, view, and delete files from your account.
            API requests are rate limited to ensure service stability.
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Key className="w-5 h-5" />
              Manage Your API Keys
            </CardTitle>
            <CardDescription>
              Create and revoke API keys for programmatic access to uploads, downloads, file management, and more
            </CardDescription>
          </CardHeader>
          <CardContent>

            <div className="mb-6 space-y-4">
              <p className="text-gray-600">
                 Simple File Upload provides two different sets of keys:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-600 ml-4">
                <li>
                  <strong>Widget upload key</strong> - A public key used in your upload widget, visible in the JavaScript snippet on the getting started page.
                </li>
                <li>
                  <strong>API keys</strong> - Consist of a user token (identifier) and secret token (password). These allow programmatic access to delete files and manage your account.
                </li>
              </ul>
              
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start">
                  <AlertCircle className="w-5 h-5 text-blue-500 mr-2 mt-0.5" />
                  <div className="text-sm text-blue-700">
                    <strong>Note:</strong> This page manages your API keys only. To find your widget upload key, visit your <a href="/account/dashboard" className="underline hover:no-underline">dashboard</a>.
                  </div>
                </div>
              </div>
              
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <div className="flex items-start">
                  <AlertCircle className="w-5 h-5 text-amber-500 mr-2 mt-0.5" />
                  <div className="text-sm text-amber-700">
                    <strong>Important:</strong> Keep your secret tokens secure. Anyone with access to these keys can delete files from your account.
                  </div>
                </div>
              </div>
            </div>

            {api_keys.length > 0 ? (
              <div className="mb-6">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>User Token</TableHead>
                      <TableHead>Secret Token</TableHead>
                      <TableHead>Last Used</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {api_keys.map((apiKey) => (
                      <TableRow key={apiKey.id}>
                        <TableCell className="font-mono text-sm">{apiKey.token}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {revealedSecrets[apiKey.id] ? (
                              <>
                                <span className="font-mono text-sm">{apiKey.secret}</span>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => toggleSecretVisibility(apiKey.id)}
                                >
                                  <EyeOff className="w-4 h-4" />
                                </Button>
                              </>
                            ) : (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => toggleSecretVisibility(apiKey.id)}
                              >
                                <Eye className="w-4 h-4 mr-2" />
                                Click to Reveal
                              </Button>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          {formatTimeAgo(apiKey.last_used_at)}
                        </TableCell>
                        <TableCell className="text-right">
                          {can_destroy && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteApiKey(apiKey.id)}
                              className="text-red-600 hover:text-red-700"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <Key className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                <p>No API keys have been generated yet.</p>
              </div>
            )}

            {can_create && (
              <div className="flex justify-center">
                <Button onClick={handleCreateApiKey}>
                  Generate New Private API Key
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </>
  );
}
